import { useCallback, useRef, useState } from "react";
import type { StudioState } from "../types";

const HISTORY_LIMIT = 60;

interface Timeline {
  past: StudioState[];
  current: StudioState;
  future: StudioState[];
}

export interface StudioHistory {
  state: StudioState;
  setState: (
    next: StudioState | ((current: StudioState) => StudioState),
    historyKey?: string
  ) => void;
  undo: () => void;
  redo: () => void;
  reset: (next: StudioState) => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useStudioHistory(initialState: StudioState): StudioHistory {
  const lastUpdate = useRef<{ key: string; at: number } | null>(null);
  const [timeline, setTimeline] = useState<Timeline>({
    past: [],
    current: initialState,
    future: []
  });

  const setState = useCallback(
    (
      next: StudioState | ((current: StudioState) => StudioState),
      historyKey?: string
    ) => {
      const now = Date.now();
      const previousUpdate = lastUpdate.current;
      const shouldCoalesce =
        Boolean(historyKey) &&
        previousUpdate !== null &&
        previousUpdate.key === historyKey &&
        now - previousUpdate.at < 500;
      lastUpdate.current = historyKey ? { key: historyKey, at: now } : null;

      setTimeline((currentTimeline) => {
        const resolved =
          typeof next === "function" ? next(currentTimeline.current) : next;
        if (JSON.stringify(currentTimeline.current) === JSON.stringify(resolved)) {
          return currentTimeline;
        }
        return {
          past: shouldCoalesce
            ? currentTimeline.past
            : [
                ...currentTimeline.past.slice(-(HISTORY_LIMIT - 1)),
                structuredClone(currentTimeline.current)
              ],
          current: resolved,
          future: []
        };
      });
    },
    []
  );

  const undo = useCallback(() => {
    lastUpdate.current = null;
    setTimeline((currentTimeline) => {
      const previous = currentTimeline.past.at(-1);
      if (!previous) return currentTimeline;
      return {
        past: currentTimeline.past.slice(0, -1),
        current: structuredClone(previous),
        future: [
          structuredClone(currentTimeline.current),
          ...currentTimeline.future
        ].slice(0, HISTORY_LIMIT)
      };
    });
  }, []);

  const redo = useCallback(() => {
    lastUpdate.current = null;
    setTimeline((currentTimeline) => {
      const next = currentTimeline.future[0];
      if (!next) return currentTimeline;
      return {
        past: [
          ...currentTimeline.past,
          structuredClone(currentTimeline.current)
        ].slice(-HISTORY_LIMIT),
        current: structuredClone(next),
        future: currentTimeline.future.slice(1)
      };
    });
  }, []);

  const reset = useCallback((next: StudioState) => {
    lastUpdate.current = null;
    setTimeline({ past: [], current: structuredClone(next), future: [] });
  }, []);

  return {
    state: timeline.current,
    setState,
    undo,
    redo,
    reset,
    canUndo: timeline.past.length > 0,
    canRedo: timeline.future.length > 0
  };
}
